import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CalendarBlank, Clock, ArrowRight, ArrowUpRight } from '@phosphor-icons/react';
import { useBlogPosts } from '../hooks/useBlog';
import { CardSkeleton } from '../components/SkeletonLoader';
import './BeritaSection.css';

// Calculate reading time
const calculateReadingTime = (content) => {
  if (!content) return 1;
  const wordsPerMinute = 200;
  const text = content.replace(/<[^>]*>/g, '');
  const wordCount = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
};

const BeritaCard = ({ berita, index }) => {
  const cardRef = useRef(null);
  const parallaxRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    const parallax = parallaxRef.current;
    if (!card || !parallax) return;

    // Subtle parallax on scroll
    const handleScroll = () => {
      const rect = card.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isVisible && !card.matches(':hover')) {
        const scrollY = window.scrollY;
        const speed = 0.02; // Reduced speed for less movement
        const yPos = scrollY * speed * (index % 2 === 0 ? 1 : -1) * 0.3;
        parallax.style.transform = `translateY(${yPos}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [index]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <article 
      ref={cardRef}
      className="news-card stagger-item"
      style={{ animationDelay: `${0.1 + index * 0.1}s` }}
    >
      <Link to={`/blog/${berita.slug}`} className="news-card-link">
        {/* Image Container - FULL FILL */}
        <div className="image-wrapper">
          {/* Parallax container - larger than wrapper to allow movement */}
          <div ref={parallaxRef} className="parallax-container">
            <img
              src={berita.image_url || 'https://via.placeholder.com/800x600?text=Bahrul+Ulum'}
              alt={berita.title}
              className="news-image"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x600?text=Bahrul+Ulum';
              }}
            />
          </div>
          <div className="image-overlay"></div>
          
          {/* Date Badge */}
          <div className="date-badge">
            <CalendarBlank weight="fill" className="date-icon" />
            {formatDate(berita.created_at)}
          </div>
          
          {/* Read Circle */}
          <div className="read-circle">
            <ArrowRight weight="bold" className="read-icon" />
          </div>
          
          {/* Category Tag */}
          <div className="category-tag">
            <span>Berita</span>
          </div>
        </div>
        
        {/* Card Content */}
        <div className="card-content">
          {/* Meta Info */}
          <div className="meta-info">
            <span className="meta-item">
              <Clock weight="fill" className="meta-icon" />
              {calculateReadingTime(berita.content)} menit baca
            </span>
          </div>
          
          {/* Title */}
          <h3 className="news-title">
            {berita.title}
          </h3>
          
          {/* Excerpt */}
          <p className="news-excerpt">
            {berita.excerpt || berita.title}
          </p>
          
          {/* Read More */}
          <div className="read-more">
            Baca Selengkapnya
            <ArrowRight weight="bold" className="read-more-icon" />
          </div>
        </div>
      </Link>
    </article>
  );
};

const BeritaSection = () => {
  const sectionRef = useRef(null);
  const { posts, loading, error } = useBlogPosts();
  
  // Get only first 3 posts for homepage
  const beritaData = posts.slice(0, 3);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = sectionRef.current?.querySelectorAll('.stagger-item, .header-animate');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <section id="news" className="berita-section" ref={sectionRef}>
        <div className="container-tight">
          <div className="berita-header header-animate">
            <div className="berita-header-content">
              <div className="berita-header-left">
                <span className="berita-label">BERITA & ARTIKEL</span>
                <h2 className="berita-title">
                  Update Terkini<br/>
                  <span className="berita-title-muted">dari Bahrul Ulum</span>
                </h2>
              </div>
            </div>
          </div>
          <CardSkeleton count={3} />
        </div>
      </section>
    );
  }

  if (error || beritaData.length === 0) {
    return (
      <section id="news" className="berita-section" ref={sectionRef}>
        <div className="container-tight">
          <div className="berita-header header-animate">
            <div className="berita-header-content">
              <div className="berita-header-left">
                <span className="berita-label">BERITA & ARTIKEL</span>
                <h2 className="berita-title">
                  Update Terkini<br/>
                  <span className="berita-title-muted">dari Bahrul Ulum</span>
                </h2>
              </div>
              <Link to="/blog" className="berita-link-all">
                <span>Lihat Semua Berita</span>
                <ArrowUpRight weight="bold" className="berita-link-icon" />
              </Link>
            </div>
          </div>
          <div className="berita-empty">
            <p>Belum ada berita tersedia. Silakan kunjungi halaman blog untuk melihat semua artikel.</p>
            <Link to="/blog" className="berita-empty-link">
              Lihat Blog <ArrowRight weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="news" className="berita-section" ref={sectionRef}>
      <div className="container-tight">
        {/* Section Header */}
        <div className="berita-header header-animate">
          <div className="berita-header-content">
            <div className="berita-header-left">
              <span className="berita-label">BERITA & ARTIKEL</span>
              <h2 className="berita-title">
                Update Terkini<br/>
                <span className="berita-title-muted">dari Bahrul Ulum</span>
              </h2>
            </div>
            <Link to="/blog" className="berita-link-all">
              <span className="berita-link-text">Lihat Semua Berita</span>
              <ArrowUpRight weight="bold" className="berita-link-icon" />
            </Link>
          </div>
        </div>

        {/* News Grid */}
        <div className="berita-grid">
          {beritaData.map((berita, index) => (
            <BeritaCard key={berita.id} berita={berita} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeritaSection;
